// OBJECTIVES RELATED
import React from "react";
import { renderToString } from "react-dom/server";
import { hexToRgba } from "../optionsHelper";
import { renderSampleStatusTrend } from "../helper";
import Banner from "../../../../b2b-common/Banner";

export function getDiamondShape(x, y, width, height, color) {
    return (
        {
            type: 'path',
            shape: {
                pathData: 'M 0 -5 L 5 0 L 0 5 L -5 0 Z', // Diamond shape
                x: x,
                y: y,
                width: width,
                height: height,
            },
            style: {
                fill: color,
            },
        }
    );
}

export function getLine(x1, y1, x2, y2, color, lineWidth = 1, lineDash = []) {
    return (
        {
            type: 'line',
            shape: {
                x1: x1,
                y1: y1,
                x2: x2,
                y2: y2,
            },
            style: {
                stroke: color,
                lineWidth: lineWidth,
                lineDash: lineDash,
            },
        }
    );
}

// ITEMS AND ARROWS AMONG THEM

const colorMap = {
    0: '#777777',
    1: '#5b85ff',
    2: '#d0c762',
    3: '#c92727',
    4: '#41aa45',
    5: '#4B0082',
    6: '#EE82EE',
};

export const renderSampleItem = (params, api) => {
    const categoryIndex = api.value(0); // Access category using index 0
    const startTime = api.coord([api.value(1), categoryIndex]); // Access startTime using index 1
    const endTime = api.coord([api.value(2), categoryIndex]); // Access endTime using index 2
    const barHeight = api.size([0, 1])[1] * 0.55; // Adjust the height ratio as needed

    return {
        type: 'rect',
        shape: {
            x: startTime[0],
            y: startTime[1] - barHeight / 2,
            width: endTime[0] - startTime[0],
            height: barHeight,
            r: [5, 5],
        },
        style: {
            fill: hexToRgba(colorMap[categoryIndex], 0.25) || 'black',
            stroke: colorMap[categoryIndex] || 'black',
            lineWidth: 1,
        },
    };
};

export const renderLastSampleItem = (params, api) => {
    const categoryIndex = api.value(0); // Access category using index 0
    const startTime = api.coord([api.value(1), categoryIndex]); // Access startTime using index 1
    const endTime = api.coord([api.value(2), categoryIndex]); // Access endTime using index 2
    const barHeight = api.size([0, 1])[1] * 0.55; // Adjust the height ratio as needed

    return {
        type: 'rect',
        shape: {
            x: startTime[0],
            y: startTime[1] - barHeight / 2,
            width: endTime[0] - startTime[0],
            height: barHeight,
            r: [5, 5],
        },
        style: {
            fill: hexToRgba(colorMap[categoryIndex], 0.15) || 'black',
            stroke: colorMap[categoryIndex] || 'black',
            lineWidth: 0,
        },
    };
};

export const renderObjectiveItem = (params, api) => {
    const startCategoryIndex = api.value(0); // Start category index
    const startDateTime = api.coord([api.value(1), startCategoryIndex]); // Start date time
    const endCategoryIndex = api.value(2); // End category index
    const endDateTime = api.coord([api.value(3), endCategoryIndex]); // End date time

    const categoryHeight = api.size([0, 1])[1]; // Height of a category in pixels
    const objectiveColor = hexToRgba('#3233c1', 0.5);

    return {
        type: 'group',
        children: [
            getLine(startDateTime[0], startDateTime[1],
                startDateTime[0], startDateTime[1] + (categoryHeight * (startCategoryIndex - endCategoryIndex)),
                objectiveColor, 1, [2, 2]),
            getLine(startDateTime[0], startDateTime[1] + (categoryHeight * (startCategoryIndex - endCategoryIndex)),
                endDateTime[0], startDateTime[1] + (categoryHeight * (startCategoryIndex - endCategoryIndex)),
                objectiveColor, 1, [2, 2]),
            getDiamondShape(startDateTime[0] - 5, startDateTime[1] - 5, 10, 10, objectiveColor),
            getDiamondShape(endDateTime[0] - 5, endDateTime[1] - 5, 10, 10, objectiveColor),
        ],
    };
};

export const renderArrowItem = (params, api) => {
    const startCategoryIndex = api.value(0); // Start category index
    const endCategoryIndex = api.value(3); // End category index

    const categoryDelta = (endCategoryIndex - startCategoryIndex);

    if (startCategoryIndex === endCategoryIndex) return; // No arrow if same category
    if (endCategoryIndex === null || isNaN(endCategoryIndex)) return; // No arrow if no next category or if it's NaN

    const currentSampleEndTime = api.coord([api.value(2), startCategoryIndex]); // End time of the current sample
    const nextSampleStartTime = api.coord([api.value(1), endCategoryIndex]); // Start time of the next sample

    const midX = (currentSampleEndTime[0] + nextSampleStartTime[0]) / 2; // Midpoint X coordinate
    const controlPointOffset = 5; // Offset for control points
    const categoryHeight = api.size([0, 1])[1]; // Height of a category in pixels
    const arrowHeadSize = 5;
    return {
        type: 'path',
        shape: {
            pathData: `
                    M ${currentSampleEndTime[0]} ${currentSampleEndTime[1]} 
                    L ${currentSampleEndTime[0] + controlPointOffset} ${currentSampleEndTime[1]}
                    C ${currentSampleEndTime[0] + controlPointOffset} ${currentSampleEndTime[1]} ${currentSampleEndTime[0] + controlPointOffset} ${currentSampleEndTime[1] - ((categoryHeight * categoryDelta) / 2)} ${currentSampleEndTime[0] + controlPointOffset} ${currentSampleEndTime[1] - ((categoryHeight * categoryDelta) / 2)}
                    L ${midX} ${currentSampleEndTime[1] - ((categoryHeight * categoryDelta) / 2)}
                    C ${midX} ${currentSampleEndTime[1] - ((categoryHeight * categoryDelta) / 2)} ${midX} ${nextSampleStartTime[1]} ${midX} ${nextSampleStartTime[1]}
                    L ${midX + (currentSampleEndTime[0] - nextSampleStartTime[0]) / 2} ${nextSampleStartTime[1]}
                    M ${midX + (currentSampleEndTime[0] - nextSampleStartTime[0]) / 2} ${nextSampleStartTime[1]}
                    L ${midX + (currentSampleEndTime[0] - nextSampleStartTime[0]) / 2 - arrowHeadSize} ${nextSampleStartTime[1] - arrowHeadSize}
                    M ${midX + (currentSampleEndTime[0] - nextSampleStartTime[0]) / 2} ${nextSampleStartTime[1]}
                    L ${midX + (currentSampleEndTime[0] - nextSampleStartTime[0]) / 2 - arrowHeadSize} ${nextSampleStartTime[1] + arrowHeadSize}
                `,
        },
        style: {
            stroke: colorMap[startCategoryIndex] || 'black',
            lineWidth: 1,
            // lineDash: [3, 3],
            fill: 'none',
        },
    };
};

// TOOLTIP FORMATTERS

export const renderSampleTooltip = (date, value, status, trend) => {
    // console.log('renderSampleTooltip', date, value, status, trend);
    return renderToString(
        <div className={`qualitative-sample-tooltip-container status_${status}`}>
            <Banner message='Sample' underline={false} headingLevel='h5' className="qualitative-sample-tooltip-banner" />
            <Banner message={value} underline={false} headingLevel='h6' className="qualitative-sample-tooltip-banner" />
            <div className="qualitative-tooltip-data-date">
                <div className="qualitative-tooltip-data-date-label">Date</div>
                <div className="qualitative-tooltip-data-date-value">{date}</div>
            </div>
            {renderSampleStatusTrend(status, trend)}
        </div>
    );
}

export const renderObjectiveTooltip = (startDate, startValue, endDate, endValue) => {
    return renderToString(
        <div className="qualitative-objective-tooltip-container">
            <Banner message='Objective' underline={false} headingLevel='h5' className="qualitative-objective-tooltip-banner" />
            <Banner sectionTittle={'Start: '} message={`${startValue}`} underline={false} headingLevel='h6' className="qualitative-objective-tooltip-banner" />
            <Banner sectionTittle={'End: '} message={`${endValue}`} underline={false} headingLevel='h6' className="qualitative-objective-tooltip-banner" />
            <div className="qualitative-objective-tooltip-dates">
                <div className="qualitative-tooltip-data-date">
                    <div className="qualitative-tooltip-data-date-label">Start Date</div>
                    <div className="qualitative-tooltip-data-date-value">{startDate}</div>
                </div>
                <div className="qualitative-tooltip-data-date">
                    <div className="qualitative-tooltip-data-date-label">End Date</div>
                    <div className="qualitative-tooltip-data-date-value">{endDate}</div>
                </div>
            </div>
        </div>
    );
}